import { Checkbox, Form, InputNumber } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import type { NamePath } from 'rc-field-form/es/interface';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import { useStore } from 'zustand';

import { inventoryStore } from '../../../../InventoryStore';
import { useLabelValueCellStore } from '../../../../hooks';
import { DuplicationMenu } from '../../../DuplicationMenu/DuplicationMenu';
import { onHover } from '../LabelValueCell/utils';

import styles from './StockInputCell.module.scss';

export type StockInputCellProps = {
  name: NamePath;
  date: string;
  disabled?: boolean;
  grayed?: boolean;
  label?: string | number;
  onDuplicate?: () => void;
  hasError?: boolean;
};

type StockInputFieldProps = {
  value?: number | null;
  onChange?: (value: number | null) => void;
  disabled?: boolean;
  children?: ReactNode;
  hasError?: boolean;
};

const StockInputField = ({
  value = null,
  onChange,
  disabled,
  children,
  hasError = false,
}: StockInputFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isUnlimited = value === null;

  const handleUnlimitedChange = (checked: boolean) => {
    onChange?.(checked ? null : 0);

    if (!checked) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  return (
    <>
      <div className={styles.inputRow}>
        <InputNumber
          ref={inputRef}
          className={classNames(styles.inputNumber, {
            [styles.disabled]: disabled,
          })}
          value={value}
          onChange={onChange}
          precision={0}
          type="number"
          min={0}
          placeholder={hasError ? '0' : '∞'}
          disabled={disabled}
          onWheel={(e) => e.currentTarget.blur()}
          controls={false}
        />
        {children}
      </div>
      <div className={styles.checkboxWrapper}>
        <div className={styles.checkboxRow}>
          <Checkbox
            checked={isUnlimited}
            disabled={disabled}
            onChange={(e) => handleUnlimitedChange(e.target.checked)}
          />{' '}
          ∞
        </div>
      </div>
    </>
  );
};

export const StockInputCell = ({
  name,
  onDuplicate,
  date,
  label,
  disabled,
  grayed,
  hasError = false,
}: StockInputCellProps) => {
  const isEditMode = useStore(inventoryStore, (state) => state.isEditMode);
  const { timePeriod, setDuplicationDateSelection } = useLabelValueCellStore();

  return (
    <div
      className={classNames(styles.stockInputCell, { [styles.grayed]: grayed })}
    >
      {!isEditMode && <span className={styles.label}>{label}</span>}
      {isEditMode && (
        <Form.Item
          name={name}
          getValueProps={(value) => ({ value: value ?? null })}
          getValueFromEvent={(value) => value ?? null}
          noStyle
        >
          <StockInputField disabled={disabled} hasError={hasError}>
            {onDuplicate && (
              <DuplicationMenu
                date={dayjs(date)}
                currentRange={{
                  start: dayjs(timePeriod.startDate),
                  end: dayjs(timePeriod.endDate),
                }}
                onDuplicate={onDuplicate}
                onHover={(day, mode) => {
                  onHover(day, mode, timePeriod, setDuplicationDateSelection);
                }}
                onHoverEnd={() => setDuplicationDateSelection([])}
              />
            )}
          </StockInputField>
        </Form.Item>
      )}
    </div>
  );
};
